import { useState, useMemo, useCallback } from 'react';
import { type AppState, type Feature, type Participant } from './types';
import { ItemInput } from './components/ItemInput';
import { PurchasingView } from './components/PurchasingView';
import { ResultsView } from './components/ResultsView';
import { LiveSessionSetup } from './components/LiveSessionSetup';
import { LivePurchasingView } from './components/LivePurchasingView';
import { LiveResultsView } from './components/LiveResultsView';
import { Users, User } from 'lucide-react';
import ToolWrapper, { type ToolHostProps, type ToolResultItem } from '../../components/prioritization/ToolWrapper';
import { useToolMode, useToolSettings, useToolSituationalContext, type ToolItem } from '../../contexts/toolItemsHooks';
import { useAttributesStore } from '../../stores/prioritization/attributesStore';
import {
  buyAFeatureBudget,
  loadSessionPrices,
  lowerPledge,
  priceItemsByEffort,
  raisePledge,
  toEffortAttribute,
} from '../../lib/buyAFeaturePricing';
import { useLiveToolSession } from '../../components/prioritization/session/liveExperience/useLiveToolSession';
import { LiveToolScaffold } from '../../components/prioritization/session/liveExperience/LiveToolScaffold';
import {
  BuyAFeatureLive,
  type BuyAFeatureLiveItem,
} from '../../components/prioritization/session/liveExperience/liveTools/BuyAFeatureLive';
import { useCollaborativeGame } from '../../hooks/useCollaborativeGame';
import { updateSessionStatus, recordEvent } from '../../lib/collaborativeGameService';

type PlayMode = 'solo' | 'live';

interface FeatureInput {
  name: string;
  price: number;
}

function BuyAFeatureContent(props: ToolHostProps) {
  const toolMode = useToolMode();
  const { isLiveSession, importSourceLabel } = useLiveToolSession();
  const isAppMode = toolMode === 'app';
  // Share of the total price handed out as budget — the session's setting
  // (the wizard's "Budget as % of total item value"); 40 percent by default.
  const { budgetPercentage } = useToolSettings();
  const budgetPct = Math.round(budgetPercentage * 100);
  const situationalContext = useToolSituationalContext();
  const effortDefinition = useAttributesStore((state) => state.getAttributeByKey('effort'));
  
  const [playMode, setPlayMode] = useState<PlayMode | null>(null);
  const [state, setState] = useState<AppState>('input');
  const [featureInputs, setFeatureInputs] = useState<FeatureInput[]>([]);
  const [numParticipants, setNumParticipants] = useState(1);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [currentParticipantIndex, setCurrentParticipantIndex] = useState(0);

  // In a session each person pledges toward items (pooling, owner 2026-10-28):
  // what they have put toward each item, by feature id.
  const [livePledges, setLivePledges] = useState<Record<string, number>>({});

  // Live session state
  const [liveSessionId, setLiveSessionId] = useState<string | null>(null);
  const [liveMyName, setLiveMyName] = useState('');
  const [liveIsHost, setLiveIsHost] = useState(false);
  const [liveState, setLiveState] = useState<'setup' | 'playing' | 'results'>('setup');

  // Track source IDs for imported items
  const [sourceIdMap, setSourceIdMap] = useState<Record<string, string>>({});

  // Handler for importing items from workspace.
  // An item's price comes from its official effort (lib/buyAFeaturePricing):
  // the middle of its value range is about $20, each step doubles or halves
  // it, varied a little per session and item. A guest is handed the efforts by
  // the guest room; a member reads them.
  const handleImportItems = useCallback(async (toolItems: ToolItem[]) => {
    const ids = toolItems.map((item, index) => item.sourceId ?? item.id ?? `feature-${index}`);
    const readout = situationalContext?.sessionReadout;
    const seed = situationalContext?.sessionId ?? 'standalone';
    const prices = readout
      ? priceItemsByEffort(ids, readout.itemEfforts ?? {}, readout.effortAttribute ?? null, seed)
      : await loadSessionPrices(ids, seed, toEffortAttribute(effortDefinition));

    const newInputs = toolItems.map((item, idx) => ({
      name: item.name,
      price: prices[ids[idx]],
    }));
    setFeatureInputs(prev => [...prev, ...newInputs]);
    
    // Track source IDs
    const newSourceIdMap: Record<string, string> = {};
    toolItems.forEach((item, idx) => {
      const featureId = `feature-${featureInputs.length + idx}`;
      if (item.sourceId) {
        newSourceIdMap[featureId] = item.sourceId;
      }
    });
    setSourceIdMap(prev => ({ ...prev, ...newSourceIdMap }));

    if (isLiveSession && toolItems.length > 0) {
      const newFeatures: Feature[] = toolItems.map((item, index) => ({
        id: ids[index],
        name: item.name,
        price: prices[ids[index]],
        purchases: 0,
        totalSpent: 0,
      }));
      // Each person is given the configured share (40% by default) of the total price.
      const budget = buyAFeatureBudget(prices, budgetPercentage);
      setFeatures(newFeatures);
      setParticipants([
        {
          id: 'live-participant',
          name: 'You',
          budget,
          remainingBudget: budget,
          purchases: [],
        },
      ]);
      setCurrentParticipantIndex(0);
      setState('purchasing');
    }
  }, [featureInputs.length, isLiveSession, budgetPercentage, situationalContext, effortDefinition]);
  
  // Live session game state (only active when liveSessionId is set)
  const game = useCollaborativeGame(liveSessionId, liveMyName);

  const handleSessionJoined = useCallback((sessionId: string, myName: string, isHost: boolean) => {
    setLiveSessionId(sessionId);
    setLiveMyName(myName);
    setLiveIsHost(isHost);
    setLiveState('playing');
  }, []);

  const handleEndLiveSession = useCallback(async () => {
    if (!liveSessionId || !liveMyName) return;
    await updateSessionStatus(liveSessionId, 'ended');
    await recordEvent(liveSessionId, liveMyName, 'session_ended', {});
    setLiveState('results');
  }, [liveSessionId, liveMyName]);

  const handleResetLive = useCallback(() => {
    setPlayMode(null);
    setLiveSessionId(null);
    setLiveMyName('');
    setLiveIsHost(false);
    setLiveState('setup');
  }, []);

  // Get results for saving as session result
  const handleGetResults = useCallback((): ToolResultItem[] => {
    if (isLiveSession) {
      // A person's answer is what they pledged toward each item. Everyone's
      // pledges add up (the counting rule sums), and an item is bought when the
      // room's total reaches its price.
      return [...features]
        .sort((a, b) => (livePledges[b.id] ?? 0) - (livePledges[a.id] ?? 0))
        .map((feature, idx) => ({
          id: sourceIdMap[feature.id] || feature.id,
          name: feature.name,
          rank: idx + 1,
          score: livePledges[feature.id] ?? 0,
          metadata: { price: feature.price, pledged: livePledges[feature.id] ?? 0 },
        }));
    }
    // Sort by total spent (most purchased first)
    const sortedFeatures = [...features].sort((a, b) => b.totalSpent - a.totalSpent);
    
    return sortedFeatures.map((feature, idx) => ({
      id: sourceIdMap[feature.id] || feature.id,
      name: feature.name,
      rank: idx + 1,
      score: feature.totalSpent,
      scoreBreakdown: {
        price: feature.price,
        purchases: feature.purchases,
        totalSpent: feature.totalSpent,
      },
      metadata: {
        price: feature.price,
        purchases: feature.purchases,
        totalSpent: feature.totalSpent,
      }
    }));
  }, [features, sourceIdMap, isLiveSession, livePledges]);

  // Total budget: the configured share of the total price
  const totalPrice = useMemo(() => {
    return featureInputs.reduce((sum, f) => sum + f.price, 0);
  }, [featureInputs]);

  const totalBudget = useMemo(() => {
    return totalPrice * budgetPercentage;
  }, [totalPrice, budgetPercentage]);

  const budgetPerParticipant = useMemo(() => {
    return numParticipants > 0 ? totalBudget / numParticipants : 0;
  }, [totalBudget, numParticipants]);

  // Initialize features and participants when starting
  const handleStart = () => {
    const newFeatures: Feature[] = featureInputs.map((input, index) => ({
      id: `feature-${index}`,
      name: input.name,
      price: input.price,
      purchases: 0,
      totalSpent: 0,
    }));

    const newParticipants: Participant[] = Array.from({ length: numParticipants }, (_, index) => ({
      id: `participant-${index}`,
      name: `Participant ${index + 1}`,
      budget: budgetPerParticipant,
      remainingBudget: budgetPerParticipant,
      purchases: [],
    }));

    setFeatures(newFeatures);
    setParticipants(newParticipants);
    setCurrentParticipantIndex(0);
    setState('purchasing');
  };

  // Handle purchase
  const handlePurchase = (featureId: string, participantId: string) => {
    const feature = features.find(f => f.id === featureId);
    const participant = participants.find(p => p.id === participantId);

    if (!feature || !participant || participant.remainingBudget < feature.price) {
      return;
    }

    // Update participant
    setParticipants(prev => prev.map(p => {
      if (p.id === participantId) {
        const existingPurchase = p.purchases.find(pur => pur.featureId === featureId);
        const newPurchases = existingPurchase
          ? p.purchases.map(pur =>
              pur.featureId === featureId
                ? { ...pur, quantity: pur.quantity + 1 }
                : pur
            )
          : [...p.purchases, { featureId, quantity: 1 }];

        return {
          ...p,
          remainingBudget: p.remainingBudget - feature.price,
          purchases: newPurchases,
        };
      }
      return p;
    }));

    // Update feature
    setFeatures(prev => prev.map(f => {
      if (f.id === featureId) {
        return {
          ...f,
          purchases: f.purchases + 1,
          totalSpent: f.totalSpent + f.price,
        };
      }
      return f;
    }));
  };

  // Handle remove purchase
  const handleRemovePurchase = (featureId: string, participantId: string) => {
    const feature = features.find(f => f.id === featureId);
    const participant = participants.find(p => p.id === participantId);

    if (!feature || !participant) {
      return;
    }

    const purchase = participant.purchases.find(p => p.featureId === featureId);
    if (!purchase || purchase.quantity === 0) {
      return;
    }

    // Update participant
    setParticipants(prev => prev.map(p => {
      if (p.id === participantId) {
        const newPurchases = p.purchases.map(pur =>
          pur.featureId === featureId
            ? { ...pur, quantity: Math.max(0, pur.quantity - 1) }
            : pur
        ).filter(pur => pur.quantity > 0);

        return {
          ...p,
          remainingBudget: p.remainingBudget + feature.price,
          purchases: newPurchases,
        };
      }
      return p;
    }));

    // Update feature
    setFeatures(prev => prev.map(f => {
      if (f.id === featureId) {
        return {
          ...f,
          purchases: Math.max(0, f.purchases - 1),
          totalSpent: Math.max(0, f.totalSpent - f.price),
        };
      }
      return f;
    }));
  };

  // Handle next participant
  const handleNextParticipant = () => {
    if (currentParticipantIndex < participants.length - 1) {
      setCurrentParticipantIndex(prev => prev + 1);
    }
  };

  // Handle finish
  const handleFinish = () => {
    if (!isLiveSession) {
      setState('results');
    }
  };

  const liveParticipant = participants[0];
  const liveFeatures: BuyAFeatureLiveItem[] =
    features.map(f => ({
      id: f.id,
      name: f.name,
      price: f.price,
      pledged: livePledges[f.id] ?? 0,
    }));
  const livePledged = Object.values(livePledges).reduce((sum, v) => sum + v, 0);
  const liveRemaining = liveParticipant ? Math.max(0, liveParticipant.budget - livePledged) : 0;
  const raiseLivePledge = (featureId: string) => {
    const feature = features.find(f => f.id === featureId);
    if (!feature) return;
    setLivePledges(prev => ({ ...prev, [featureId]: raisePledge(prev[featureId] ?? 0, feature.price, liveRemaining) }));
  };
  const lowerLivePledge = (featureId: string) => {
    setLivePledges(prev => ({ ...prev, [featureId]: lowerPledge(prev[featureId] ?? 0) }));
  };
  const canSubmitLive =
    isLiveSession && state === 'purchasing' && liveParticipant != null;

  // Reset to start over
  const handleReset = () => {
    setState('input');
    setFeatureInputs([]);
    setNumParticipants(1);
    setFeatures([]);
    setParticipants([]);
    setCurrentParticipantIndex(0);
    setSourceIdMap({});
  };

  return (
    <ToolWrapper
      toolName="Buy A Feature"
      toolDescription="Allocate budget to purchase the most important features"
      onImportItems={handleImportItems}
      onGetResults={canSubmitLive || state === 'results' ? handleGetResults : undefined}
      itemCount={featureInputs.length}
      {...props}
    >
      <div className="w-full">

        {/* ── Live session flows ─────────────────────────────────────── */}
        {playMode === 'live' && liveState === 'setup' && (
          <LiveSessionSetup
            featureInputs={featureInputs}
            budgetPerParticipant={budgetPerParticipant}
            totalBudget={totalBudget}
            onSessionJoined={handleSessionJoined}
            onBack={() => setPlayMode(null)}
          />
        )}

        {playMode === 'live' && liveState === 'playing' && liveSessionId && (
          <div className="flex flex-col h-[calc(100vh-120px)] min-h-[500px]">
            <LivePurchasingView
              game={game}
              myName={liveMyName}
              isHost={liveIsHost}
              onEndSession={handleEndLiveSession}
            />
          </div>
        )}

        {playMode === 'live' && liveState === 'results' && (
          <div className="p-4">
            <LiveResultsView
              game={game}
              onReset={handleResetLive}
            />
          </div>
        )}

        {isLiveSession && state === 'purchasing' && liveParticipant && (
          <LiveToolScaffold
            title="Buy a feature"
            subtitle="Pool your money with the room to buy what matters most"
          >
            <BuyAFeatureLive
              features={liveFeatures}
              budget={liveParticipant.budget}
              remainingBudget={liveRemaining}
              onRaise={raiseLivePledge}
              onLower={lowerLivePledge}
            />
          </LiveToolScaffold>
        )}

        {/* ── Solo flows ─────────────────────────────────────────────── */}
        {playMode !== 'live' && !isLiveSession && (
          <div>
            {state === 'input' && (
              <>
                {/* App mode */}
                {isAppMode && (
                  <div className="max-w-2xl mx-auto">
                    <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
                      {featureInputs.length === 0 ? (
                        <>
                          <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">
                            Configure Buy A Feature
                          </h2>
                          <p className="text-[var(--text-sec)] mb-6">
                            Set the number of participants, then import items. Together, participants get {budgetPct}% of total item prices as budget.
                          </p>
                          <div className="mb-6">
                            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
                              Number of Participants
                            </label>
                            <input
                              type="number"
                              value={numParticipants}
                              onChange={(e) => setNumParticipants(Math.max(1, parseInt(e.target.value) || 1))}
                              min="1"
                              max="20"
                              className="w-full px-4 py-3 border border-[var(--rule)] focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-transparent text-lg"
                            />
                          </div>
                          <p className="text-sm text-[var(--text-ter)]">
                            {`Import items from your ${importSourceLabel} using the button in the header.`}
                          </p>
                        </>
                      ) : (
                        <>
                          <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-2">
                            {featureInputs.length} features ready — how do you want to play?
                          </h2>
                          <div className="mb-4 p-3 bg-[var(--surface)] space-y-1 text-sm">
                            <div><span className="text-[var(--text-sec)]">Total feature value: </span><span className="font-medium">${totalPrice}</span></div>
                            <div><span className="text-[var(--text-sec)]">Budget per participant: </span><span className="font-medium">${budgetPerParticipant.toFixed(0)}</span></div>
                          </div>

                          {/* Mode selector */}
                          {playMode === null ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
                              <button
                                onClick={() => { setPlayMode('solo'); handleStart(); }}
                                className="flex items-start gap-3 p-4 border-2 border-[var(--rule)] hover:border-consulting-slate-blue-400 hover:bg-[var(--slate-dim)] transition-all text-left"
                              >
                                <div className="w-9 h-9 bg-[var(--hover-tint)] flex items-center justify-center shrink-0">
                                  <User className="h-5 w-5 text-[var(--text-sec)]" />
                                </div>
                                <div>
                                  <div className="font-semibold text-[var(--text-pri)] text-sm">Solo / same device</div>
                                  <div className="text-xs text-[var(--text-ter)] mt-0.5">Pass the screen between participants</div>
                                </div>
                              </button>
                              <button
                                onClick={() => setPlayMode('live')}
                                className="flex items-start gap-3 p-4 border-2 border-[var(--rule)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)] transition-all text-left"
                              >
                                <div className="w-9 h-9 bg-[var(--slate-dim)] flex items-center justify-center shrink-0">
                                  <Users className="h-5 w-5 text-[var(--text-ter)]" />
                                </div>
                                <div>
                                  <div className="font-semibold text-[var(--text-pri)] text-sm">Live session</div>
                                  <div className="text-xs text-[var(--text-ter)] mt-0.5">Everyone buys simultaneously — transcript preserved</div>
                                </div>
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={handleStart}
                              className="w-full px-6 py-3 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 transition-colors font-medium text-lg"
                            >
                              Start Purchasing
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
                {/* Standalone mode */}
                {!isAppMode && (
                  <ItemInput
                    features={featureInputs}
                    numParticipants={numParticipants}
                    onFeaturesChange={setFeatureInputs}
                    onParticipantsChange={setNumParticipants}
                    onStart={handleStart}
                    budgetPercentage={budgetPercentage}
                  />
                )}
              </>
            )}

            {state === 'purchasing' && participants.length > 0 && (
              <PurchasingView
                features={features}
                participants={participants}
                currentParticipantIndex={currentParticipantIndex}
                onPurchase={handlePurchase}
                onRemovePurchase={handleRemovePurchase}
                onNextParticipant={handleNextParticipant}
                onFinish={handleFinish}
              />
            )}

            {state === 'results' && (
              <ResultsView
                features={features}
                participants={participants}
                onReset={handleReset}
              />
            )}
          </div>
        )}

      </div>
    </ToolWrapper>
  );
}

export default function BuyAFeature(props: ToolHostProps) {
  return <BuyAFeatureContent {...props} />;
}
